import { useQueryClient } from '@tanstack/react-query'
import { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { useSystems, type Subscriber } from '@/hooks/use-systems'
import { type User } from '@/models/domain/user'
import { useAuthStore } from '@/stores/auth-store'
import { useListStateStore } from '@/stores/list-state-store'

function hasMultipleSystemAccess(user: User): boolean {
  return user.sys_op || user.systems_all || (user.systems != null && user.systems.length > 1)
}

function toSelectOptions(subscribers: Subscriber[]): SearchableSelectOption[] {
  return subscribers.map((s) => ({ id: s.name, name: s.display_name || s.name }))
}

function resolveDisplayName(subscribers: Subscriber[], selectedSystem: string | null): string {
  return (
    subscribers.find((s) => s.name === selectedSystem)?.display_name ||
    selectedSystem ||
    'Select system'
  )
}

function SingleSystemBadge({ displayName, className }: { displayName: string; className: string }) {
  return (
    <div
      className={`max-w-[10rem] sm:max-w-none truncate px-3 py-1.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full ${className}`}
      title={displayName}
    >
      System: <span className="font-semibold">{displayName}</span>
    </div>
  )
}

export function SystemSelector({ className }: { className?: string }) {
  const { user, selectedSystem, selectSystem } = useAuthStore()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const resetAllSlices = useListStateStore((s) => s.resetAllSlices)

  const multiAccess = !!user && hasMultipleSystemAccess(user)
  const { data: subscribers = [], isLoading } = useSystems(multiAccess)

  const options = useMemo(() => toSelectOptions(subscribers), [subscribers])

  const handleSystemChange = useCallback(
    (systemName: string) => {
      if (!systemName) return
      selectSystem(systemName)
      queryClient.removeQueries()
      resetAllSlices()
      navigate('/', { replace: true })
    },
    [selectSystem, queryClient, resetAllSlices, navigate]
  )

  if (!user) return null

  const displayName = resolveDisplayName(subscribers, selectedSystem)

  if (!multiAccess) {
    return <SingleSystemBadge displayName={displayName} className={className ?? ''} />
  }

  return (
    <div className={`w-32 min-[430px]:w-40 sm:w-56 ${className ?? ''}`}>
      <SearchableSelect
        options={options}
        value={selectedSystem ?? ''}
        onChange={handleSystemChange}
        placeholder="Select system..."
        isLoading={isLoading}
      />
    </div>
  )
}
